import { useGetPartnerReceiverInfo } from '@/hooks/useReceivers';
import Modal from '../ui/Modal';
import Loader from '../ui/Loader';
import { PartnerReceiverInfo } from '@/api/receiver.api';
import React from 'react';
import { useTranslation } from '@/i18n';

interface ReceiverInfoModalProps {
  receiverId: string;
  closeModal: () => void;
}

const ReceiverInfoModal = ({
  receiverId,
  closeModal,
}: ReceiverInfoModalProps) => {
  const { t } = useTranslation();
  const {
    data: receiverInfo,
    isLoading,
    isSuccess,
  } = useGetPartnerReceiverInfo(receiverId);

  return (
    <Modal
      actionOnClick={() => {}}
      actionTitle=""
      closeModal={closeModal}
      title={t('receivers.modals.receiverInfo.title')}
      hasAction={false}
      cancelBtnLabel={t('receivers.modals.receiverInfo.close')}
    >
      {isLoading ? (
        <Loader />
      ) : (
        isSuccess && (
          <ReceiverInfoModalData
            receiverInfo={receiverInfo}
            receiverId={receiverId}
          />
        )
      )}
    </Modal>
  );
};

export default ReceiverInfoModal;

interface ReceiverInfoModalDataProps {
  receiverInfo: PartnerReceiverInfo;
  receiverId: string;
}

const ReceiverInfoModalData = ({
  receiverInfo,
  receiverId,
}: ReceiverInfoModalDataProps) => {
  const { t } = useTranslation();
  return (
    <>
      <DisplayField
        name={t('receivers.modals.receiverInfo.name')}
        data={receiverInfo.name}
      />
      <div className="grid grid-cols-2 gap-4">
        <DisplayField
          name={t('receivers.modals.receiverInfo.address')}
          data={receiverInfo.address}
        />
        <DisplayField
          name={t('receivers.modals.receiverInfo.city')}
          data={receiverInfo.city}
        />
        <DisplayField
          name={t('receivers.modals.receiverInfo.postalCode')}
          data={receiverInfo.postalCode}
        />
        <DisplayField
          name={t('receivers.modals.receiverInfo.country')}
          data={receiverInfo.country}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        {receiverInfo.User.map((user, indexUser) => (
          <React.Fragment key={`receiver-${receiverId}-user-${indexUser}`}>
            <DisplayField
              name={t('receivers.modals.receiverInfo.directorFirstName')}
              data={user?.firstname}
            />
            <DisplayField
              name={t('receivers.modals.receiverInfo.directorLastName')}
              data={user?.lastname}
            />
            <DisplayField
              name={t('receivers.modals.receiverInfo.directorEmail')}
              data={user?.email}
            />
            <DisplayField
              name={t('receivers.modals.receiverInfo.directorPhone')}
              data={user?.phoneNumber}
            />
          </React.Fragment>
        ))}
      </div>
      {receiverInfo.reservationEmails.map(
        (reservationEmail, indexReservationEmail) => (
          <DisplayField
            name={t('receivers.modals.receiverInfo.reservationEmail', {
              index: indexReservationEmail + 1,
            })}
            data={reservationEmail}
            key={`receiver-${receiverId}-reservation-email-${indexReservationEmail}`}
          />
        ),
      )}
    </>
  );
};

const DisplayField = ({
  name,
  data,
}: {
  name: string;
  data: string | null;
}) => {
  return (
    data && (
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-gray-800">{name}</span>
        <span className="text-sm text-gray-800">{data}</span>
      </div>
    )
  );
};
